import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { faListCheck } from '@fortawesome/pro-light-svg-icons/faListCheck';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { SMLEmptyState } from '../../components/SMLEmptyState/SMLEmptyState';
import { useFailuresSeen } from './assetsFailuresSeen';
import { useWorkLists, WORK_DONE_DAYS } from './useWorkLists';
import { workPageMessages } from './workPageMessages';
import { WorkSection } from './WorkSection';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Work.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface WorkScreenProps {
  accountId: number;
  /** The song of the link: the page then shows the work of this song only. */
  catalogItemUuid?: string;
  /** The file of the link (`result=`, `reel=`): its row has a mark. Empty: none. */
  linkedUuid: string;
}

/**
 * The body of the Work page (owner, 2026-10-11; design assets-rethink e1, e2, f1..f7): the asset
 * generations of the artist that are queued or made now, the open failures with "Try again", and
 * the files that finished in the last 7 days with a link to their home. Posts and the Kit tab keep
 * their own working and failed cards.
 */
const WorkScreenComponent: React.FC<WorkScreenProps> = ({ accountId, catalogItemUuid, linkedUuid }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isDemo = useIsDemoAccount();
  const lists = useWorkLists({ accountId, catalogItemUuid });
  const catalog = useGetCatalogQuery({ accountId });
  const catalogByUuid = useMemo(
    () => new Map((catalog.data?.items ?? []).map((item) => [item.uuid, item])),
    [catalog.data]
  );
  const now = useMemo(() => new Date(), []);
  const { songs } = lists;
  const songTitleOf = useCallback(
    (uuid: string | null) =>
      (uuid === null ? undefined : (songs[uuid]?.title ?? catalogByUuid.get(uuid)?.title)) ??
      intl.formatMessage(workPageMessages.pageNoSong),
    [catalogByUuid, intl, songs]
  );

  // The Work menu badge counts the open failures: the page that shows one marks it seen.
  const { markSeen } = useFailuresSeen(accountId);
  const failedCount = lists.failed.length;
  useEffect(() => {
    if (failedCount > 0) {
      markSeen();
    }
  }, [failedCount, markSeen]);

  if (lists.isLoading) {
    return <LoadingView />;
  }
  if (lists.error !== undefined) {
    return <ErrorView error={lists.error as Error} retry={lists.refetch} />;
  }

  const shared = { accountId, songTitleOf, catalogByUuid, readOnly: isDemo, linkedUuid, now };
  const isEmpty = lists.busy.length === 0 && failedCount === 0 && lists.done.length === 0;
  return (
    <div className={styles.page} data-testid="work-screen">
      {catalogItemUuid !== undefined && (
        // design: f4. A link that names a song keeps the work of that song.
        <p className={clsx(styles.filter, 'm-0')} data-testid="work-one-song">
          <span className={styles.songChip}>{songTitleOf(catalogItemUuid)}</span>
          <span>{intl.formatMessage(workPageMessages.pageOneSong, { song: songTitleOf(catalogItemUuid) })}</span>
          <button type="button" className={styles.link} onClick={() => router.push(SMLRoutes.promoteWork)}>
            {intl.formatMessage(workPageMessages.pageAllSongs)}
          </button>
        </p>
      )}
      {isEmpty ? (
        // design: f2. The empty state of Posts; the demo artist gets no button.
        <div data-testid="work-empty">
          <SMLEmptyState
            icon={faListCheck}
            title={intl.formatMessage(workPageMessages.pageEmptyTitle)}
            description={intl.formatMessage(workPageMessages.pageEmptyBody)}
            action={
              isDemo
                ? undefined
                : {
                    label: intl.formatMessage(workPageMessages.pageOpenStudio),
                    onClick: () => router.push(SMLRoutes.promoteStudio),
                  }
            }
          />
        </div>
      ) : (
        <>
          {/* The sections take the same shared props. */}
          {/* eslint-disable react/jsx-props-no-spreading */}
          <WorkSection
            {...shared}
            testID="work-busy"
            title={intl.formatMessage(workPageMessages.pageBusy)}
            pillClass={styles.pillBusy}
            emptyNote={intl.formatMessage(workPageMessages.pageNoBusy)}
            entries={lists.busy}
          />
          <WorkSection
            {...shared}
            testID="work-failed"
            title={intl.formatMessage(workPageMessages.pageFailed)}
            pillClass={styles.pillFailed}
            entries={lists.failed}
          />
          <WorkSection
            {...shared}
            testID="work-done"
            title={intl.formatMessage(workPageMessages.pageDone)}
            pillClass={styles.pillDone}
            headNote={intl.formatMessage(workPageMessages.pageDoneWindow, { days: WORK_DONE_DAYS })}
            entries={lists.done}
          />
          {/* eslint-enable react/jsx-props-no-spreading */}
        </>
      )}
    </div>
  );
};

export const WorkScreen = TypedMemo(WorkScreenComponent);
